import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FooterActionBar,
  InputField,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import { FormattedMessage, useIntl } from 'react-intl';

import './PolicyForm.css';

export interface PolicyNameUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTitle: string;
  initialDescription: string;
  /** Translated "Untitled Policy" – when initialTitle equals this, show empty in the name field */
  untitledLabel?: string;
  onSave: (title: string, description: string) => void;
}

export default function PolicyNameUpdateModal({
  isOpen,
  onClose,
  initialTitle,
  initialDescription,
  untitledLabel = 'Untitled Rule',
  onSave,
}: PolicyNameUpdateModalProps) {
  const intl = useIntl();
  const [policyName, setPolicyName] = useState(initialTitle);
  const [policyDescription, setPolicyDescription] =
    useState(initialDescription);

  useEffect(() => {
    if (isOpen) {
      setPolicyName(initialTitle === untitledLabel ? '' : initialTitle);
      setPolicyDescription(initialDescription);
    }
  }, [isOpen, initialTitle, initialDescription, untitledLabel]);

  const handleSave = () => {
    onSave(policyName, policyDescription);
    onClose();
  };

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={onClose}
      title={intl.formatMessage({
        id: '+r3jEv',
        defaultMessage: 'Update Rule Name and Description',
        description: 'Edit policy modal title',
      })}
      contentBody={
        <Box className='PolicyForm-modalBody'>
          <InputField
            id='edit-policy-name'
            name='name'
            size='large'
            label={
              <Typography kind='label' htmlFor='edit-policy-name'>
                {intl.formatMessage({
                  id: 'WttzEx',
                  defaultMessage: 'Rule name',
                  description: 'Policy name field label',
                })}
              </Typography>
            }
            value={policyName}
            setValue={setPolicyName}
            placeholder={intl.formatMessage({
              id: '0s4cdg',
              defaultMessage: 'Enter a title',
              description: 'Policy name placeholder',
            })}
            isRequired
          />
          <InputField
            id='edit-policy-description'
            name='description'
            placeholder={intl.formatMessage({
              id: 'vsIXSE',
              defaultMessage: 'Add a description',
              description: 'Policy description placeholder',
            })}
            label={
              <Typography kind='label' htmlFor='edit-policy-description'>
                {intl.formatMessage({
                  id: '1ezz2m',
                  defaultMessage: 'Description',
                  description: 'Description field label',
                })}
              </Typography>
            }
            value={policyDescription}
            setValue={setPolicyDescription}
            kind='multiline'
          />
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size='large'>
          <Box className='PolicyForm-modalFooter'>
            <Button kind='outlined' size='medium' onClick={onClose}>
              <FormattedMessage
                id='qz9XeG'
                defaultMessage='Cancel'
                description='Cancel button'
              />
            </Button>
            <Button
              kind='filled'
              size='medium'
              isDisabled={!policyName.trim()}
              onClick={handleSave}>
              <FormattedMessage
                id='+itf/D'
                defaultMessage='Save'
                description='Save button'
              />
            </Button>
          </Box>
        </FooterActionBar>
      }
    />
  );
}
